"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  AutomaticDiscountFragment,
  CurrencyCode,
  CustomCheckoutSetting,
  StoreDetailsFragment,
} from "../../generated/graphql";
import { TLanguage } from "@/i18n/i18n-settings";
import { isBrowser } from "../isBrowser";

export type StoreContextType = Omit<
  StoreDetailsFragment,
  "currency" | "locales" | "defaultLocale" | "customCheckoutSetting"
> & {
  storeId: string;
  hostname: string;
  storeUrl: string;
  currency: CurrencyCode;
  locales: TLanguage[];
  defaultLocale: TLanguage;
  template: string;
  automaticDiscounts: (AutomaticDiscountFragment | null)[];
  customCheckoutSetting:
    | CustomCheckoutSetting
    | Record<string, never>
    | null
    | undefined;
  defaultCountry: string | undefined;
};

const defaultValue: StoreContextType = {
  userId: "",
  id: "",
  storeId: "",
  name: "",
  storeUrl: "",
  hostname: "",
  handle: "",
  domain: null,
  logo: null,
  currency: CurrencyCode.Egp,
  locale: "",
  defaultLocale: "en",
  fallbackLocale: "en",
  locales: [],
  appearance: { colors: {}, fonts: [] },
  supportedLocales: [],
  contactInfo: { email: null, phone: null },
  socialMedia: [],
  maintenanceMode: {
    isEnabled: false,
    message: "",
  },
  subscription: null,
  template: "default",
  automaticDiscounts: [],
  integrationScriptControls: [],
  areReviewsActivated: false,
  storeReviewSettings: null,
  customCheckoutSetting: {},
  isWuiltShipmentActive: false,
  activatedWuiltShipmentProviders: [],
  redirects: [],
  defaultCountry: undefined,
};

const StoreContext = createContext<StoreContextType>(defaultValue);

const { Provider } = StoreContext;

const StoreProvider: React.FC<{
  value: StoreContextType;
  children?: React.ReactNode;
}> = ({ value = defaultValue, children }) => {
  const [defaultCountry, setDefaultCountry] = useState<string | undefined>(
    undefined
  );
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!isBrowser) return;
    if (window.localStorage.getItem("default-country")) {
      setDefaultCountry(
        window.localStorage.getItem("default-country") ?? undefined
      );
      return;
    }
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    fetch("https://ipgeo.wuilt.com/self")
      .then((res) => res.json())
      .then((res) => {
        localStorage.setItem("default-country", res?.country ?? "EG");
        setDefaultCountry(res?.country ?? "EG");
      })
      .catch(() => {
        setDefaultCountry("EG");
        localStorage.setItem("default-country", "EG");
      });
  }, []);
  value = {
    ...value,
    defaultCountry,
  };
  return <Provider value={value}>{children}</Provider>;
};

export const useStore = () => useContext(StoreContext);

export { StoreProvider };
